import {
  Archive,
  ArchiveRestore,
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { ArtistAvatar } from '@/components/artist-avatar';
import { ArtistDialog, type ArtistValues } from '@/components/artist-dialog';
import { clearComboboxCache } from '@/components/combobox';
import { ResponsiveTable } from '@/components/responsive-table';
import { ListPagination, ScrollArea } from '@/components/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useArtists } from '@/lib/api-hooks';
import {
  ARTIST_PAGE_SIZE_OPTIONS,
  type Artist,
  type ArtistOrderKey,
  archiveArtist,
  createArtist,
  restoreArtist,
  updateArtist,
} from '@/lib/artists';
import { type ArtistStatusFilter, useArtistsStore } from '@/lib/artists-store';
import { invalidateResource } from '@/lib/resource';

const orderOptions: Array<{ value: ArtistOrderKey; label: string }> = [
  { value: 'newest', label: 'Recently updated' },
  { value: 'name', label: 'Name (A–Z)' },
  { value: 'votes', label: 'Most votes' },
];

export function ArtistsPage() {
  const search = useArtistsStore((state) => state.search);
  const order = useArtistsStore((state) => state.order);
  const status = useArtistsStore((state) => state.status);
  const page = useArtistsStore((state) => state.page);
  const pageSize = useArtistsStore((state) => state.pageSize);
  const setSearch = useArtistsStore((state) => state.setSearch);
  const setOrder = useArtistsStore((state) => state.setOrder);
  const setStatus = useArtistsStore((state) => state.setStatus);
  const setPage = useArtistsStore((state) => state.setPage);
  const setPageSize = useArtistsStore((state) => state.setPageSize);

  const { data, isLoading } = useArtists({ search, order, status, page, pageSize });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Artist | null>(null);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  function openCreate() {
    setEditing(null);
    setDialogOpen(true);
  }

  function openEdit(artist: Artist) {
    setEditing(artist);
    setDialogOpen(true);
  }

  async function handleSubmit(values: ArtistValues) {
    if (editing) {
      await updateArtist(editing.id, values);
    } else {
      await createArtist(values);
    }
    setPage(1);
    clearComboboxCache('artists');
    invalidateResource('artist');
  }

  async function handleArchive(artist: Artist) {
    await archiveArtist(artist.id);
    clearComboboxCache('artists');
    invalidateResource('artist');
  }

  async function handleRestore(artist: Artist) {
    await restoreArtist(artist.id);
    clearComboboxCache('artists');
    invalidateResource('artist');
  }

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="relative w-full sm:w-60">
          <Search className="absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search artists"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="pl-8"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="hidden text-muted-foreground text-sm sm:inline">Order by</span>
          <Select value={order} onValueChange={(value) => setOrder(value as ArtistOrderKey)}>
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {orderOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={openCreate}>
            <Plus />
            New artist
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1">
        {(['all', 'active', 'archived'] as ArtistStatusFilter[]).map((filter) => (
          <Button
            key={filter}
            size="sm"
            variant={status === filter ? 'default' : 'ghost'}
            onClick={() => setStatus(filter)}
            className="h-8 capitalize"
          >
            {filter}
          </Button>
        ))}
      </div>

      <ScrollArea>
        <ResponsiveTable
          loading={isLoading}
          loadingLabel="Loading artists…"
          skeletonRows={pageSize}
          emptyLabel="No artists match your search."
          rows={items}
          rowKey={(artist) => artist.id}
          columns={[
            {
              key: 'artist',
              header: 'Artist',
              title: true,
              cell: (artist: Artist) => (
                <div className="flex items-center gap-3">
                  <ArtistAvatar name={artist.name} src={artist.avatarUrl} className="size-8" />
                  <Link to={`/artists/${artist.slug}`} className="font-medium hover:underline">
                    {artist.name}
                  </Link>
                  {artist.archived && (
                    <Badge
                      variant="outline"
                      className="border-transparent bg-muted text-muted-foreground"
                    >
                      Archived
                    </Badge>
                  )}
                </div>
              ),
            },
            {
              key: 'discipline',
              header: 'Discipline',
              cell: (artist: Artist) => (
                <span className="text-muted-foreground">{artist.discipline}</span>
              ),
            },
            {
              key: 'projects',
              header: 'Projects',
              align: 'right',
              secondary: true,
              cell: (artist: Artist) => artist.projects,
            },
            {
              key: 'proposals',
              header: 'Proposals',
              align: 'right',
              secondary: true,
              cell: (artist: Artist) => artist.proposals,
            },
            {
              key: 'votes',
              header: 'Votes',
              align: 'right',
              cell: (artist: Artist) => artist.votes.toLocaleString(),
            },
            {
              key: 'updated',
              header: 'Updated',
              align: 'right',
              secondary: true,
              cell: (artist: Artist) => (
                <span className="text-muted-foreground">{artist.updated}</span>
              ),
            },
          ]}
          actions={(artist) => (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger
                aria-label={`Actions for ${artist.name}`}
                className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
              >
                <MoreHorizontal className="size-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onSelect={() => openEdit(artist)}>
                  <Pencil />
                  Edit
                </DropdownMenuItem>
                {artist.archived ? (
                  <DropdownMenuItem onSelect={() => handleRestore(artist)}>
                    <ArchiveRestore />
                    Restore
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onSelect={() => handleArchive(artist)}>
                    <Archive />
                    Archive
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        />
        {(total > 0 || isLoading) && (
          <ListPagination>
            <p className="text-muted-foreground text-sm">
              {isLoading ? 'Loading…' : `Showing ${start}–${end} of ${total}`}
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground text-sm">Rows per page</span>
                <Select
                  value={String(pageSize)}
                  onValueChange={(value) => setPageSize(Number(value))}
                >
                  <SelectTrigger size="sm" className="w-[4.5rem]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ARTIST_PAGE_SIZE_OPTIONS.map((size) => (
                      <SelectItem key={size} value={String(size)}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1 || isLoading}
                >
                  <ChevronLeft />
                  Previous
                </Button>
                {Array.from({ length: totalPages }, (_, index) => index + 1).map((number) => (
                  <Button
                    key={number}
                    variant={number === page ? 'default' : 'ghost'}
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => setPage(number)}
                    disabled={isLoading}
                  >
                    {number}
                  </Button>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages || isLoading}
                >
                  Next
                  <ChevronRight />
                </Button>
              </div>
            </div>
          </ListPagination>
        )}
      </ScrollArea>

      {dialogOpen && (
        <ArtistDialog
          open
          onOpenChange={(next) => {
            if (!next) setDialogOpen(false);
          }}
          mode={editing ? 'edit' : 'create'}
          initialValues={
            editing
              ? {
                  name: editing.name,
                  discipline: editing.discipline,
                  bio: editing.bio,
                  email: editing.email,
                  avatarMediaId: editing.avatarMediaId ?? null,
                  avatarUrl: editing.avatarUrl ?? null,
                }
              : undefined
          }
          onSubmit={handleSubmit}
        />
      )}
    </div>
  );
}
